<template>
  <ValidationObserver @submit="addNewStructure">
    <UiModalContainer
      footer-classes="flex justify-end gap-3"
      :modal-show="modal"
      :modal-width="50"
      @handleClick="handleClick"
    >
      <template v-slot:header>
        {{ headingTitle }}
      </template>
      <template v-slot>
        <InputFieldWrapper>
          <InputBox
            v-model.trim="structureData.title"
            type="text"
            name="TITLE"
            title="TITLE"
            label="TITLE"
            placeholder="TITLE"
            rules="required"
          />
          <InputBox
            v-model="structureData.description"
            type="text"
            name="Description"
            title="Description"
            label="Description"
            placeholder="Description"
          />
        </InputFieldWrapper>
        <Divider />
        <div class="flex justify-end mt-6 mb-5">
          <UIButton class="px-5" type="button" @click="addNewGradeItem">Add New Grade</UIButton>
        </div>

        <InputFieldWrapper
          v-for="(grade, index) in gradingList"
          :key="index"
          custom-classes="items-start"
        >
          <InputBox
            v-model.trim="gradingList[index].title"
            type="text"
            :name="`GRADE_TITLE + ${index}`"
            title="Grade Title"
            label="Grade Title"
            :is-not-translated="true"
            placeholder="Grade Title"
            class="flex-1"
            rules="required"
          />
          <div v-if="!grade.isDefault" class="flex flex-1 gap-2 sm:gap-3 relative">
            <InputBox
              v-model.number="gradingList[index].value"
              type="text"
              :name="`GRADE_VAL + ${index}`"
              title="Grade Value"
              :is-not-translated="true"
              label="Grade Value"
              placeholder="Grade Value"
              class="flex-1"
              rules="required|numeric"
            />
            <span v-if="gradingList.length > 2" class="self-center" @click="removeGradeItem(index)">
              <icon
                class="icon cursor-pointer"
                width="12"
                height="12"
                color="mahroon-strong"
                icon="cross"
              />
            </span>
          </div>
          <div v-else class="flex flex-1 gap-3">
            <InputBox
              type="text"
              name="VALUE"
              title="Grade Value"
              label="VALUE"
              :disabled="grade.isDefault"
              :is-not-translated="true"
              :placeholder="`Less Than ${gradingList[index]['value']}`"
              class="flex-1"
            />
          </div>
        </InputFieldWrapper>
      </template>
      <template v-slot:footer>
        <UIButton v-i18n="dashboard" :disabled="isLoading" @click="handleClick('close')">
          Cancel
        </UIButton>
        <UIButton button="primary" :disabled="isLoading">
          <template v-if="isLoading">
            <Loader />
          </template>
          <template v-else>
            {{ saveBtnTitle }}
          </template>
        </UIButton>
      </template>
    </UiModalContainer>
  </ValidationObserver>
</template>

<script>
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import InputBox from '@components/UiElements/UiInputBox.vue'
import { Form as ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import Loader from '@components/BaseComponent/Loader.vue'
import Divider from '@src/components/Divider.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import icon from '@components/icons/icon.vue'
import { objectDeepCopy } from '@utils/generalUtil'
import InputFieldWrapper from '@/src/components/UiElements/InputFieldWrapper.vue'

export default {
  components: {
    UiModalContainer,
    InputBox,
    ValidationObserver,
    Loader,
    Divider,
    UIButton,
    icon,
    InputFieldWrapper,
  },
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['toggle'],
  data() {
    return {
      newGrade: { title: '', value: '', isDefault: false },
      defaultGrade: { title: '', value: '', isDefault: true },
      structureData: {
        title: '',
        description: '',
      },
      gradingList: [{ title: '', value: 100, isDefault: false }],
      isLoading: false,
    }
  },
  computed: {
    saveBtnTitle() {
      return this.edit ? 'Update' : 'Save'
    },
    headingTitle() {
      return this.edit ? 'Edit Non GPA Grading Structure' : 'Add Non GPA Grading Structure'
    },
  },
  watch: {
    gradingList: {
      deep: true,
      immediate: true,
      handler(list) {
        let values = []
        list.forEach((grade) => {
          if (!grade.isDefault && typeof grade.value === 'number') values.push(grade.value)
        })
        if (list.length > 1)
          list[list.length - 1]['value'] = values.length ? Math.min(...values) : 100
      },
    },
  },

  created() {
    if (this.edit) this.setFormDate()
    else this.gradingList = [...this.gradingList, { ...this.defaultGrade }]
  },
  methods: {
    addNewGradeItem() {
      let newGrade = objectDeepCopy(this.newGrade)
      this.gradingList.splice(this.gradingList.length - 1, 0, newGrade)
    },
    removeGradeItem(idx) {
      this.gradingList.splice(idx, 1)
    },
    handleClick(eventName) {
      switch (eventName) {
        case 'close':
          this.$emit('toggle')
          break
      }
    },
    async addNewStructure() {
      this.isLoading = true
      let payload = { structure: this.getPayload() }
      if (this.edit) payload.id = this.edit?.id
      let [res, err] = await this.addNonGPAGradingStructure(payload)
      this.isLoading = false
      if (res) this.$emit('toggle', true)
    },
    getPayload() {
      return {
        title: this.structureData.title,
        description: this.structureData.description,
        default_grade_title: this.gradingList[this.gradingList.length - 1].title,
        default_less_than: this.gradingList[this.gradingList.length - 1].value,
        grading_scales: this.getGradingScalesPayload().filter((item) => !!item),
      }
    },

    getGradingScalesPayload() {
      return this.gradingList.map((gradingItem) => {
        if (!gradingItem.isDefault)
          return { title: gradingItem.title, greater_than_equal_to: gradingItem.value }
      })
    },
    setFormDate() {
      this.structureData.title = this.edit?.title || ''
      this.structureData.description = this.edit?.description || ''
      this.gradingList = this.edit.grading_scales.map((scale) => {
        return { title: scale.title, value: scale.greater_than_equal_to, isDefault: false }
      })
      this.gradingList = [
        ...this.gradingList,
        {
          title: this.edit.default_grade_title,
          value: this.edit.default_less_than,
          isDefault: true,
        },
      ]
    },
    ...mapActions('exams', ['addNonGPAGradingStructure']),
  },
}
</script>
